/**
 * web/src/shell/boot.ts — the auth/boot gate's state and effects, moved
 * verbatim out of App.tsx (card WUHnArc6lNID) as hooks App calls in the
 * original declaration order, so effect order and dependency arrays are
 * unchanged: last-identity tracking, the socket-token sync, the thread reset
 * on sign-out, the first-act deal/dismiss pair, the parked share target, the
 * referral/promo redemption, and the Settings screen actions.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { getToken, authApi, getLastIdentity, clearLastIdentity, type LastIdentity } from '../lib/api';
import { applyPendingReferral } from '../lib/referrals';
import { applyPendingPromo } from '../lib/promo';
import { clearPendingShare, readPendingShare, type PearShare } from '../lib/shareTarget';
import { FIRST_ACT_KEY } from '../components/FirstAct';
import type { useSession } from '../hooks/useSession';
import type { useThread } from '../hooks/useThread';
import type { Dest, Tab } from './dest';

type Session = ReturnType<typeof useSession>;
type Thread = ReturnType<typeof useThread>;

export function useLastIdentity(session: Session) {
  // Card Vn4Up6kLOCc3 — recognised but signed out: who this device belongs
  // to, in email + name form only. Re-read whenever the session lifecycle
  // moves (sign-out lands on 'pair'), and dropped by "Not you?" on the pair
  // screen. Carries no token and no couple link — it cannot open anything.
  // Declared after `session` because the effect depends on its status.
  const [lastIdentity, setLastIdentity] = useState(() => getLastIdentity());
  useEffect(() => {
    setLastIdentity(getLastIdentity());
  }, [session.status]);
  const forgetIdentity = useCallback(() => {
    clearLastIdentity();
    setLastIdentity(null);
  }, []);
  return { lastIdentity, setLastIdentity, forgetIdentity };
}

export function useTokenSync(
  session: Session,
  { setToken, setTab, setSettingsOpen, setExtraView }: {
    setToken: Dispatch<SetStateAction<string | null>>;
    setTab: Dispatch<SetStateAction<Tab>>;
    setSettingsOpen: Dispatch<SetStateAction<boolean>>;
    setExtraView: Dispatch<SetStateAction<'help' | null>>;
  },
) {
  // Keep the socket token in step with the session lifecycle.
  useEffect(() => {
    setToken(session.status === 'chat' ? getToken() : null);
    if (session.status === 'pair') {
      setTab('thread');
      setSettingsOpen(false);
      setExtraView(null);
    }
  }, [session.status]);
}

export function useThreadReset(token: string | null, threadRef: { current: Thread | null }) {
  // Dropping back to the pair screen must clear the thread too.
  useEffect(() => {
    if (!token) threadRef.current?.reset();
  }, [token]);
}

export function useFirstAct(session: Session, thread: Thread) {
  // The three-choice first act (card Tw2G4BUmOF5o): what replaces the modal
  // that used to open over the app on first arrival.
  const [firstActOpen, setFirstActOpen] = useState(false);
  // First arrival in the chat: deal the three-choice first act unless it has
  // already been dealt. The tour no longer opens itself — reading nine steps
  // instead of doing one thing was the finding this card fixes. The old tour
  // flag counts as "seen" too: anyone who finished the tour (including every
  // seeded walk) has been onboarded and does not need the card.
  //
  // Card PsFeOvVtF57K (founder email 2026-10-21: "the onboarding prompt
  // (To Begin) should be suppressed if the user has already completed actions
  // in the app"): the flag is per device and forgets a reinstall, so it is not
  // the only gate. Real activity is — an account that has already written to
  // the Thread has done the thing the card exists to start, and being told
  // "three ways to start" afterwards is noise. This waits for `historyLoaded`
  // so the check runs against the messages the server actually holds instead
  // of racing the first frame, and it counts only MY messages (partner
  // messages and system lines are not this user's actions).
  const hasMyActivity = useCallback(() => {
    const mine = thread.myMemberId
      ? thread.messages.some(m => m.fromMemberId === thread.myMemberId)
      : false;
    return mine || thread.echoes.length > 0;
  }, [thread.messages, thread.echoes, thread.myMemberId]);

  useEffect(() => {
    if (session.status !== 'chat') return;
    let seen = false;
    try {
      seen = localStorage.getItem(FIRST_ACT_KEY) === '1' || localStorage.getItem('pear.onboarded') === '1';
    } catch {
      seen = true; // storage unavailable — never trap anyone in a loop
    }
    // historyLoaded is the gate on the activity check: before it lands the
    // thread is empty for everyone, and an existing account would be dealt
    // the card for the length of one round trip.
    if (!seen && thread.historyLoaded && !hasMyActivity()) setFirstActOpen(true);
  }, [session.status, thread.historyLoaded, hasMyActivity]);

  /** Dismiss the first act for good — the flag is what stops it returning. */
  const dismissFirstAct = useCallback(() => {
    try {
      localStorage.setItem(FIRST_ACT_KEY, '1');
    } catch {
      /* private mode — it will offer itself again, which is the intent */
    }
    setFirstActOpen(false);
  }, []);

  // Same rule while the card is already on screen: the first message I send
  // (or any that arrives from the server as mine) dismisses it, flag and all.
  // Declared after dismissFirstAct — the dependency array reads it.
  useEffect(() => {
    if (firstActOpen && thread.historyLoaded && hasMyActivity()) dismissFirstAct();
  }, [firstActOpen, thread.historyLoaded, hasMyActivity, dismissFirstAct]);
  return { firstActOpen, dismissFirstAct };
}

export function usePendingShare(session: Session, navigateTo: (dest: Dest) => void) {
  /* PWA share target (card jZn7vpkB7EG1): main.tsx parked the payload off
     /share before React mounted, and it is read ONCE here — held, not
     consumed, because a share that arrives signed-out has to survive sign-up
     before there is a Notebook to fill. Landing on the pair screen must not
     drop it: nothing is written to the couple's data until Save is pressed. */
  const [pendingShare, setPendingShare] = useState<PearShare | null>(() => readPendingShare());

  /* Once this device is a real couple, take the parked share to Notebook.
     Only here — the pair screen renders no panels, so routing any earlier
     would be a tab switch into a screen that is not mounted. */
  useEffect(() => {
    if (!pendingShare || session.status !== 'chat') return;
    navigateTo({ section: 'notebook' });
  }, [pendingShare, session.status, navigateTo]);

  /* The panel calls this the moment it has prefilled the bar: one-shot, so a
     tab away and back cannot re-open a capture the person already dismissed. */
  const consumePendingShare = useCallback(() => {
    clearPendingShare();
    setPendingShare(null);
  }, []);
  return { pendingShare, consumePendingShare };
}

export function useReferralRedemption(session: Session) {
  /* Referral redemption (card 3MWZ8Zs7jA7e): a friend who arrived on ?ref=
     is holding the code in localStorage, and the reward can only be redeemed
     once this device has a couple token. The one-attempt guard is keyed by the
     COUPLE, not the launch (pre-deploy review, 2026-10-17): signing out and
     forming another couple on the same phone must still be able to redeem.
     Silent on every failure — a dead or already-used link must never interrupt
     sign-up or pairing. A successful apply dispatches the event ReferralToast
     shows. */
  const referralTriedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!getToken()) return;
    let coupleId = '';
    try {
      coupleId = localStorage.getItem('pear_couple_id') ?? '';
    } catch {
      return; // storage blocked: there is no per-couple key to guard with
    }
    if (!coupleId || referralTriedFor.current === coupleId) return;
    referralTriedFor.current = coupleId;
    void applyPendingReferral();
    // Card KpYLZuhhlcvC — a promo code captured on the account-creation
    // screen rides the same moment: once this device holds a couple, redeem
    // it (silent on refusal, same guard shape as the referral above).
    void applyPendingPromo();
  }, [session.status]);
}

export function useSessionActions(
  { session, threadRef, setSettingsOpen, setShowingAI, setExtraView, setTourOpen, setLastIdentity }: {
    session: Session;
    threadRef: { current: Thread | null };
    setSettingsOpen: Dispatch<SetStateAction<boolean>>;
    setShowingAI: Dispatch<SetStateAction<boolean>>;
    setExtraView: Dispatch<SetStateAction<'help' | null>>;
    setTourOpen: Dispatch<SetStateAction<boolean>>;
    setLastIdentity: Dispatch<SetStateAction<LastIdentity | null>>;
  },
) {
  /**
   * Leave (card Vf23eSKKxDhX) — one place, one confirm, and now one that
   * actually happens on the server. Delegates the whole sequence to
   * session.leaveCouple(): the endpoint first, then the local drop. Returns
   * null on success or the sentence to show, so Settings can keep the person
   * in the couple with the server's reason when it refuses (a live
   * subscription, most likely). Only a real success closes this screen.
   */
  const leaveCouple = useCallback(async (): Promise<string | null> => {
    const failed = await session.leaveCouple();
    if (failed) return failed;
    threadRef.current?.reset();
    setSettingsOpen(false);
    return null;
  }, [session]);

  /**
   * Sign out (Jonathan email 2026-10-07: "there's no sign out only leave").
   * Revokes the ACCOUNT session server-side first — while the token is still
   * in localStorage — then drops the local couple + auth state and lands on
   * the pair screen. Nothing on the couple changes: the next sign-in with
   * email + password mints a fresh couple token and returns to the room.
   */
  const signOut = async () => {
    try {
      await authApi('/api/auth/logout', { method: 'POST' });
    } catch {
      /* session already dead server-side — the local clear is what matters */
    }
    threadRef.current?.reset();
    session.leave();
    // Card Vn4Up6kLOCc3 — leave() just recorded who signed out; refresh the
    // identity the pair screen greets them with (the effect above only runs
    // when the status MOVES, and a sign-out that started on 'pair' doesn't).
    setLastIdentity(getLastIdentity());
    setSettingsOpen(false);
  };

  /** Close settings and open the AI panel in place. */
  const onOpenAI = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(true);
  }, []);

  /** Close settings and open the full Help inventory (card FiLhF6LRwAAP). */
  const onOpenHelp = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView('help');
  }, []);

  /** The tour is on demand, and it lives with Help inside Settings now
   *  (card yOszUUb0Nnc2) instead of taking a More-menu row of its own. */
  const onOpenTour = useCallback(() => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView(null);
    setTourOpen(true);
  }, []);
  return { leaveCouple, signOut, onOpenAI, onOpenHelp, onOpenTour };
}
